import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Database, 
  CheckCircle, 
  AlertTriangle, 
  Copy, 
  ExternalLink,
  RefreshCw,
  Info
} from 'lucide-react';
// Removed study room indexes import

const FirebaseIndexHelper = () => {
  const [copiedIndex, setCopiedIndex] = useState(null);

  const copyToClipboard = async (text, indexName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(indexName);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      // Failed to copy to clipboard
    }
  };

  const generateFirestoreRules = () => {
    return `// Firestore Security Rules for Live Classes
rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    // Live Classes Collection
    match /liveClasses/{classId} {
      allow read: if true; // Anyone can read active classes
      allow write: if request.auth != null && 
        (request.auth.uid == resource.data.creatorId || 
         request.auth.token.admin == true);
    }
    
    // Room Requests Collection
    match /roomRequests/{requestId} {
      allow read: if request.auth != null;
      allow create: if request.auth != null && 
        request.auth.uid == request.resource.data.creatorId;
      allow update: if request.auth != null && 
        request.auth.token.admin == true;
    }
    
    // Room Participants Collection
    match /roomParticipants/{participantId} {
      allow read: if request.auth != null;
      allow write: if request.auth != null && 
        request.auth.uid == resource.data.userId;
    }
    
    // Room Moderation Collection
    match /roomModeration/{moderationId} {
      allow read: if request.auth != null;
      allow write: if request.auth != null && 
        request.auth.token.admin == true;
    }
    
    // Room Reports Collection
    match /roomReports/{reportId} {
      allow read: if request.auth != null && 
        request.auth.token.admin == true;
      allow create: if request.auth != null;
    }
  }
}`;
  };

  const indexConfigs = [
    {
      name: 'Active Live Classes',
      collection: 'liveClasses',
      fields: [
        { field: 'status', order: 'ASCENDING' },
        { field: 'isActive', order: 'ASCENDING' },
        { field: 'createdAt', order: 'DESCENDING' }
      ],
      description: 'Required for querying active study rooms with sorting'
    },
    {
      name: 'Pending Room Requests',
      collection: 'roomRequests',
      fields: [
        { field: 'status', order: 'ASCENDING' },
        { field: 'createdAt', order: 'DESCENDING' }
      ],
      description: 'Required for querying pending room requests with sorting'
    },
    {
      name: 'Room Participants',
      collection: 'roomParticipants',
      fields: [
        { field: 'roomId', order: 'ASCENDING' },
        { field: 'isActive', order: 'ASCENDING' }
      ],
      description: 'Required for querying room participants'
    },
    {
      name: 'Moderation History',
      collection: 'roomModeration',
      fields: [
        { field: 'roomId', order: 'ASCENDING' },
        { field: 'timestamp', order: 'DESCENDING' }
      ],
      description: 'Required for querying moderation history'
    },
    {
      name: 'Room Reports',
      collection: 'roomReports',
      fields: [
        { field: 'targetUserId', order: 'ASCENDING' },
        { field: 'status', order: 'ASCENDING' }
      ],
      description: 'Required for querying room reports'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center space-x-3">
          <Database className="h-8 w-8" />
          <div>
            <h2 className="text-2xl font-bold">Firebase Index Setup</h2>
            <p className="text-blue-100">Configure Firebase indexes for Study Rooms functionality</p>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Important</h3>
            <p className="text-sm text-yellow-700 mt-1">
              These indexes are required for the Study Rooms feature to work properly. 
              Without them, you may see "failed-precondition" errors when joining rooms.
            </p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Info className="h-5 w-5 mr-2" />
          Setup Instructions
        </h3>
        <div className="space-y-4 text-sm text-gray-700">
          <div className="flex items-start space-x-3">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">1</span>
          </div>
          <div className="flex items-start space-x-3">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">2</span>
            <p>Click "Create Index" for each index configuration below</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">3</span>
            <p>Copy the configuration details and paste them into Firebase Console</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">4</span>
            <p>Wait for indexes to build (this may take a few minutes)</p>
          </div>
        </div>
      </div>

      {/* Index Configurations */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Required Indexes</h3>
        {indexConfigs.map((config, index) => (
          <motion.div
            key={config.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white border border-gray-200 rounded-lg p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-medium text-gray-900">{config.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{config.description}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => copyToClipboard(JSON.stringify(config, null, 2), config.name)}
                  className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                >
                  <Copy className="h-4 w-4" />
                  <span>{copiedIndex === config.name ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Collection:</span>
                  <span className="ml-2 text-gray-900">{config.collection}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Fields:</span>
                  <div className="mt-1 space-y-1">
                    {config.fields.map((field, fieldIndex) => (
                      <div key={fieldIndex} className="text-gray-900">
                        • {field.field} ({field.order})
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Firestore Rules */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Firestore Security Rules</h3>
            <p className="text-sm text-gray-600 mt-1">
              Optional: Update your Firestore security rules for better access control
            </p>
          </div>
          <button
            onClick={() => copyToClipboard(generateFirestoreRules(), 'rules')}
            className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
          >
            <Copy className="h-4 w-4" />
            <span>{copiedIndex === 'rules' ? 'Copied!' : 'Copy Rules'}</span>
          </button>
        </div>
        
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-green-400 text-sm">
            <code>{generateFirestoreRules()}</code>
          </pre>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Database className="h-6 w-6 text-blue-600" />
            <div>
              <div className="font-medium text-blue-900">Firebase Console</div>
              <div className="text-sm text-blue-700">Manage your project</div>
            </div>
            <ExternalLink className="h-4 w-4 text-blue-600 ml-auto" />
          </a>
          
          <a
            href="https://firebase.google.com/docs/firestore/query-data/indexing"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <CheckCircle className="h-6 w-6 text-green-600" />
            <div>
              <div className="font-medium text-green-900">Indexing Guide</div>
              <div className="text-sm text-green-700">Learn about Firestore indexes</div>
            </div>
            <ExternalLink className="h-4 w-4 text-green-600 ml-auto" />
          </a>
        </div>
      </div>

      {/* Status Check */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Index Status Check</h3>
        <p className="text-sm text-gray-600 mb-4">
          After creating the indexes, you can test if they're working by trying to join a study room.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh Page</span>
        </button>
      </div>
    </div>
  );
};

export default FirebaseIndexHelper;
